/**
 * Which columns the contact table shows, and the control that changes it.
 *
 * The directory holds far more per contact than seven columns can carry, and
 * which of it matters is the reader's business, not the table's: a desk
 * calling brokers wants phone and email on screen, one reconciling records
 * wants city, state and LinkedIn. So the set is a preference, stored per
 * browser, with a Name column that is never removed — a row with no
 * name is not a row anyone can use.
 */
import { useEffect, useMemo, useRef, useState, type HTMLAttributes } from 'react'
import { Columns3, Check, GripVertical, ArrowUp, ArrowDown } from 'lucide-react'
import clsx from 'clsx'

import type { CrmContact } from '@/services/crm'
import type { RelationshipRow } from '@/services/crmRelationships'
import { NOT_ON_RECORD, relativeTime } from './crmFormat'
import type { ContactSortField } from './crmFormat'

/** `sort` names the sort field when
 *  the column has one; a column without it is display-only. */
export type ContactColumnKey =
  | 'name'
  | 'company'
  | 'type'
  | 'stage'
  | 'health'
  | 'deals'
  | 'activity'
  | 'lastContact'
  | 'score'
  | 'title'
  | 'email'
  | 'phone'
  | 'mobile'
  | 'address'
  | 'city'
  | 'state'
  | 'linkedin'

export interface ContactColumn {
  key: ContactColumnKey
  label: string
  sort?: ContactSortField
  /** Shown until someone says otherwise. */
  defaultOn: boolean
  /** The cell as text. The relationship columns (stage, health, 12 months)
   *  read the server's relationship row for the contact; without one - a
   *  retired row, a board that could not be read - they say so with a dash. */
  value: (c: CrmContact, row?: RelationshipRow) => string
}

const dash = (v: string | null | undefined) => (v && String(v).trim() ? String(v) : NOT_ON_RECORD)

export const CONTACT_COLUMNS: ContactColumn[] = [
  { key: 'name', label: 'Name', sort: 'name', defaultOn: true, value: (c) => c.displayName },
  { key: 'company', label: 'Company', sort: 'company', defaultOn: true, value: (c) => dash(c.company) },
  { key: 'type', label: 'Type', sort: 'type', defaultOn: true, value: (c) => c.companyType },
  { key: 'stage', label: 'Stage', sort: 'stage', defaultOn: true, value: (_c, row) => row?.stageTitle ?? '—' },
  { key: 'health', label: 'Health', sort: 'health', defaultOn: true, value: (_c, row) => (row?.health == null ? '—' : String(row.health)) },
  { key: 'deals', label: 'Deals', sort: 'deals', defaultOn: true, value: (c) => String(c.linkedDeals.length) },
  { key: 'activity', label: '12 mo', sort: 'activity', defaultOn: true, value: (_c, row) => (row ? String(row.spark.reduce((sum, n) => sum + n, 0)) : '—') },
  { key: 'lastContact', label: 'Last contact', sort: 'lastContact', defaultOn: true, value: (c) => relativeTime(c.lastContact) },
  { key: 'score', label: 'Score', sort: 'score', defaultOn: false, value: (c) => (c.score == null ? '—' : String(c.score)) },
  { key: 'title', label: 'Title', defaultOn: false, value: (c) => dash(c.title) },
  { key: 'email', label: 'Email', defaultOn: false, value: (c) => dash(c.email) },
  { key: 'phone', label: 'Phone', defaultOn: false, value: (c) => dash(c.phone) },
  { key: 'mobile', label: 'Mobile', defaultOn: false, value: (c) => dash(c.phones?.mobile) },
  { key: 'address', label: 'Address', defaultOn: false, value: (c) => dash(c.address) },
  { key: 'city', label: 'City', defaultOn: false, value: (c) => dash(c.city) },
  { key: 'state', label: 'State', defaultOn: false, value: (c) => dash(c.state) },
  { key: 'linkedin', label: 'LinkedIn', defaultOn: false, value: (c) => dash(c.enrichment?.linkedIn) },
]

export const DEFAULT_CONTACT_COLUMNS: ContactColumnKey[] = CONTACT_COLUMNS.filter((c) => c.defaultOn).map((c) => c.key)


/** Preserve saved order, discard stale keys and migrate the old implicit Name column. */
export function normalizeContactColumns(raw: unknown[]): ContactColumnKey[] {
  const keys = [...new Set(raw.filter((key): key is ContactColumnKey => CONTACT_COLUMNS.some((c) => c.key === key)))]
  return keys.includes('name') ? keys : ['name', ...keys]
}

export function moveContactColumn(keys: ContactColumnKey[], from: ContactColumnKey, to: ContactColumnKey) {
  if (from === to || !keys.includes(from) || !keys.includes(to)) return keys
  const next = keys.filter((key) => key !== from)
  next.splice(keys.indexOf(to), 0, from)
  return next
}

/** Shared native drag behavior for table headings and menu rows. */
export function useColumnReorder(keys: ContactColumnKey[], onChange?: (keys: ContactColumnKey[]) => void) {
  const source = useRef<ContactColumnKey | null>(null)
  const suppressClick = useRef(false)
  const [target, setTarget] = useState<ContactColumnKey | null>(null)
  const move = (key: ContactColumnKey, delta: number) => {
    const to = keys[keys.indexOf(key) + delta]
    if (to) onChange?.(moveContactColumn(keys, key, to))
  }
  const props = (key: ContactColumnKey): HTMLAttributes<HTMLElement> => onChange ? {
    draggable: true,
    title: 'Drag to reorder; Alt + arrow keys to move',
    tabIndex: 0,
    onDragStart: (event) => {
      source.current = key
      suppressClick.current = true
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', key)
    },
    onDragOver: (event) => {
      if (!source.current) return
      event.preventDefault()
      event.dataTransfer.dropEffect = 'move'
      setTarget(key)
    },
    onDrop: (event) => {
      if (!source.current) return
      event.preventDefault()
      onChange(moveContactColumn(keys, source.current, key))
      source.current = null
      setTarget(null)
    },
    onDragEnd: () => { source.current = null; setTarget(null) },
    onPointerDown: () => { suppressClick.current = false },
    onClickCapture: (event) => { if (suppressClick.current) { event.preventDefault(); event.stopPropagation(); suppressClick.current = false } },
    onKeyDown: (event) => {
      if (!event.altKey || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return
      event.preventDefault()
      move(key, event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1)
    },
  } : {}
  return { props, target, move }
}

const STORAGE_KEY = 'cmos.crm.columns.v2'
const LEGACY_STORAGE_KEY = 'cmos.crm.columns.v1'

/** The default set before the relationship columns existed. */
const LEGACY_DEFAULT_COLUMNS: ContactColumnKey[] = ['name', 'company', 'type', 'deals', 'lastContact', 'score']

/**
 * What a saved v1 column set becomes.
 *
 * The v1 key was written on every first visit, so a saved set is NOT evidence
 * that anybody chose it. A set identical to the old default was never
 * customised: it takes the new default, relationship columns included. A set
 * the person changed is theirs and is left exactly as it was - the new columns
 * are one tick away in the Columns menu.
 */
export function migrateLegacyColumns(saved: ContactColumnKey[]): ContactColumnKey[] {
  const untouched =
    saved.length === LEGACY_DEFAULT_COLUMNS.length && LEGACY_DEFAULT_COLUMNS.every((key, i) => saved[i] === key)
  return untouched ? DEFAULT_CONTACT_COLUMNS : saved
}

function readStoredColumns(): ContactColumnKey[] {
  const storage = window.localStorage
  const current = storage.getItem(STORAGE_KEY)
  if (current) {
    const parsed = JSON.parse(current)
    return Array.isArray(parsed) ? normalizeContactColumns(parsed) : DEFAULT_CONTACT_COLUMNS
  }
  const legacy = storage.getItem(LEGACY_STORAGE_KEY)
  if (legacy) {
    const parsed = JSON.parse(legacy)
    return Array.isArray(parsed) ? migrateLegacyColumns(normalizeContactColumns(parsed)) : DEFAULT_CONTACT_COLUMNS
  }
  return DEFAULT_CONTACT_COLUMNS
}

/** The chosen columns, remembered in this browser.
 *
 *  Storage is a convenience, never load-bearing: a private window, cleared
 *  site data or a browser that refuses storage all fall back to the default
 *  set rather than to an empty table. */
export function useContactColumns(): [ContactColumnKey[], (next: ContactColumnKey[]) => void] {
  const [keys, setKeys] = useState<ContactColumnKey[]>(() => {
    try {
      return readStoredColumns()
    } catch {
      return DEFAULT_CONTACT_COLUMNS
    }
  })

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(keys))
    } catch {
      /* storage unavailable — the choice lives for this session only */
    }
  }, [keys])

  return [keys, setKeys]
}

/** The toolbar control: a popover of checkboxes over the registry. */
export function ColumnPicker({
  selected,
  onChange,
}: {
  selected: ContactColumnKey[]
  onChange: (next: ContactColumnKey[]) => void
}) {
  const [open, setOpen] = useState(false)
  const wrap = useRef<HTMLDivElement>(null)
  const chosen = useMemo(() => new Set(selected), [selected])
  const reorder = useColumnReorder(selected, onChange)
  const ordered = [...selected.map((key) => CONTACT_COLUMNS.find((c) => c.key === key)!), ...CONTACT_COLUMNS.filter((c) => !chosen.has(c.key))]

  useEffect(() => {
    if (!open) return
    const away = (event: MouseEvent) => {
      if (!wrap.current?.contains(event.target as Node)) setOpen(false)
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', away)
    document.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('mousedown', away)
      document.removeEventListener('keydown', escape)
    }
  }, [open])

  const toggle = (key: ContactColumnKey) => {
    const next = chosen.has(key) ? selected.filter((k) => k !== key) : [...selected, key]
    onChange(next)
  }

  return (
    <div ref={wrap} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="true"
        title="Choose which columns the table shows"
        className={clsx(
          'flex items-center gap-1 whitespace-nowrap rounded-md border px-2.5 py-1 text-[11px] font-medium transition-colors',
          open
            ? 'border-amber-400/30 bg-amber-400/[0.10] text-amber-200'
            : 'border-amber-400/10 bg-black/40 text-stone-300 hover:text-amber-200',
        )}
      >
        <Columns3 size={12} /> Columns
        <span className="font-mono text-[9px] text-stone-500">{selected.length}</span>
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-8 z-40 w-56 rounded-lg border border-amber-300/15 bg-[#111] p-1.5 shadow-xl"
        >
          <div className="px-2 py-1 text-[9px] uppercase tracking-[0.16em] text-stone-600">Columns · drag to reorder</div>
          <div className="max-h-[19rem] overflow-y-auto scrollbar-thin">
            {ordered.map((column) => {
              const on = chosen.has(column.key)
              return (
                <div key={column.key} {...(on ? reorder.props(column.key) : {})}
                  data-column-key={column.key}
                  className={clsx('flex items-center rounded', on && 'cursor-grab', reorder.target === column.key && 'bg-amber-400/15 ring-1 ring-amber-300/50')}>
                  <GripVertical size={12} className={on ? 'shrink-0 text-stone-500' : 'shrink-0 text-transparent'} aria-hidden="true" />
                  <button type="button" role="menuitemcheckbox" aria-checked={on}
                    disabled={column.key === 'name'} onClick={() => toggle(column.key)}
                    className="flex min-w-0 flex-1 items-center gap-2 rounded px-1 py-1 text-left text-[11px] text-stone-300 hover:bg-white/[0.04]">
                    <Check size={11} className={on ? 'text-amber-300' : 'text-transparent'} aria-hidden="true" />
                    {column.label}
                    {column.key === 'name' && <span className="text-[9px] text-stone-500">always</span>}
                  </button>
                  {on && <>
                    <button type="button" aria-label={`Move ${column.label} earlier`} disabled={selected.indexOf(column.key) === 0}
                      onClick={() => reorder.move(column.key, -1)} className="p-1 text-stone-400 hover:text-amber-200 disabled:opacity-20"><ArrowUp size={11} /></button>
                    <button type="button" aria-label={`Move ${column.label} later`} disabled={selected.indexOf(column.key) === selected.length - 1}
                      onClick={() => reorder.move(column.key, 1)} className="p-1 text-stone-400 hover:text-amber-200 disabled:opacity-20"><ArrowDown size={11} /></button>
                  </>}
                </div>
              )
            })}
          </div>
          <button
            type="button"
            onClick={() => onChange(DEFAULT_CONTACT_COLUMNS)}
            className="mt-1 w-full rounded px-2 py-1 text-left text-[10px] text-stone-500 hover:bg-white/[0.04] hover:text-stone-300"
          >
            Reset to default
          </button>
        </div>
      ) : null}
    </div>
  )
}
